import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  collection, query, where, orderBy, onSnapshot,
  doc, setDoc, getDoc, updateDoc, deleteDoc, serverTimestamp, getCountFromServer,
} from "firebase/firestore";
import { db } from "../firebase";

function getYouTubeId(url) {
  const m = url?.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([^&?/\s]+)/);
  return m ? m[1] : null;
}

export default function FinanceCommunity() {
  const navigate = useNavigate();
  const { slug } = useParams();

  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [subscriberCount, setSubscriberCount] = useState(0);
  const [showJoin, setShowJoin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [authorAvatar, setAuthorAvatar] = useState("");
  const [userEmail, setUserEmail] = useState(() => localStorage.getItem("fc_email") || null);
  const [likedPosts, setLikedPosts] = useState([]);
  const [votedPolls, setVotedPolls] = useState(() => {
    try { return JSON.parse(localStorage.getItem("fc_voted") || "{}"); } catch { return {}; }
  });

  useEffect(() => {
    if (!slug) { setNotFound(true); setLoading(false); return; }
    const q = query(collection(db, "communities"), where("slug", "==", slug));
    return onSnapshot(q, (snap) => {
      if (snap.empty) { setNotFound(true); setLoading(false); return; }
      const commDoc = snap.docs[0];
      setCommunity({ id: commDoc.id, ...commDoc.data() });
    });
  }, [slug]);

  useEffect(() => {
    if (!community) return;
    const q = query(
      collection(db, "community_posts"),
      where("communityId", "==", community.id),
      orderBy("createdAt", "desc")
    );
    let countsLoaded = false;
    const unsub = onSnapshot(q, async (snap) => {
      const docs = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((p) => (p.status || "published") === "published");
      if (!countsLoaded) {
        countsLoaded = true;
        const entries = await Promise.all(
          docs.map(async (p) => {
            try {
              const s = await getCountFromServer(collection(db, "community_posts", p.id, "likes"));
              return [p.id, s.data().count];
            } catch { return [p.id, p.likeCount || 0]; }
          })
        );
        const countMap = Object.fromEntries(entries);
        setPosts(docs.map((p) => ({ ...p, likeCount: countMap[p.id] ?? p.likeCount ?? 0 })));
      } else {
        setPosts((prev) => {
          const prevCounts = Object.fromEntries(prev.map((p) => [p.id, p.likeCount]));
          return docs.map((d) => ({ ...d, likeCount: prevCounts[d.id] ?? d.likeCount ?? 0 }));
        });
      }
      setLoading(false);
    }, (err) => { console.error("Posts query failed:", err); setLoading(false); });

    getCountFromServer(collection(db, "communities", community.id, "subscribers"))
      .then((s) => setSubscriberCount(s.data().count))
      .catch(() => setSubscriberCount(0));

    return () => unsub();
  }, [community?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getDoc(doc(db, "settings", "profile"))
      .then((snap) => { if (snap.exists()) setAuthorAvatar(snap.data().avatarUrl || ""); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!userEmail || posts.length === 0) return;
    let cancelled = false;
    Promise.all(
      posts.map((p) =>
        getDoc(doc(db, "community_posts", p.id, "likes", userEmail))
          .then((snap) => (snap.exists() ? p.id : null))
          .catch(() => null)
      )
    ).then((results) => { if (cancelled) return; setLikedPosts(results.filter(Boolean)); });
    return () => { cancelled = true; };
  }, [userEmail, posts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoined = (email) => {
    localStorage.setItem("fc_email", email);
    setUserEmail(email);
    setSubscriberCount((c) => c + 1);
  };

  const handleLike = useCallback(async (postId) => {
    if (!userEmail) { setShowJoin(true); return; }
    const isLiked = likedPosts.includes(postId);
    const newLiked = isLiked ? likedPosts.filter((id) => id !== postId) : [...likedPosts, postId];
    setLikedPosts(newLiked);
    setPosts((prev) => prev.map((p) => p.id === postId
      ? { ...p, likeCount: Math.max(0, (p.likeCount || 0) + (isLiked ? -1 : 1)) } : p));
    try {
      const likeRef = doc(db, "community_posts", postId, "likes", userEmail);
      if (isLiked) {
        await deleteDoc(likeRef);
      } else {
        const existing = await getDoc(likeRef);
        if (!existing.exists()) await setDoc(likeRef, { email: userEmail, likedAt: serverTimestamp() });
      }
    } catch (err) {
      console.error("Like failed:", err);
      setLikedPosts(likedPosts);
      setPosts((prev) => prev.map((p) => p.id === postId
        ? { ...p, likeCount: Math.max(0, (p.likeCount || 0) + (isLiked ? 1 : -1)) } : p));
    }
  }, [userEmail, likedPosts]);

  const handleVote = useCallback(async (postId, optionIndex) => {
    if (!userEmail) { setShowJoin(true); return; }
    if (votedPolls[postId] !== undefined) return;
    const newVoted = { ...votedPolls, [postId]: optionIndex };
    setVotedPolls(newVoted);
    localStorage.setItem("fc_voted", JSON.stringify(newVoted));
    setPosts((prev) => prev.map((p) => {
      if (p.id !== postId) return p;
      return { ...p, pollOptions: p.pollOptions.map((o, i) => i === optionIndex ? { ...o, votes: (o.votes || 0) + 1 } : o) };
    }));
    try {
      const postRef = doc(db, "community_posts", postId);
      const voteRef = doc(db, "community_posts", postId, "votes", userEmail);
      const voteSnap = await getDoc(voteRef);
      if (voteSnap.exists()) throw new Error("already voted");
      const postSnap = await getDoc(postRef);
      const opts = postSnap.data().pollOptions.map((o, i) =>
        i === optionIndex ? { ...o, votes: (o.votes || 0) + 1 } : o
      );
      await updateDoc(postRef, { pollOptions: opts });
      await setDoc(voteRef, { optionIndex, email: userEmail, votedAt: serverTimestamp() });
    } catch (err) {
      console.error("Vote failed:", err);
      const revert = { ...votedPolls };
      delete revert[postId];
      setVotedPolls(revert);
      localStorage.setItem("fc_voted", JSON.stringify(revert));
      setPosts((prev) => prev.map((p) => {
        if (p.id !== postId) return p;
        return { ...p, pollOptions: p.pollOptions.map((o, i) => i === optionIndex ? { ...o, votes: Math.max(0, (o.votes || 0) - 1) } : o) };
      }));
    }
  }, [userEmail, votedPolls]);

  if (notFound) {
    return (
      <div className="fc fc--center">
        <style>{CSS}</style>
        <div className="fc-empty">
          <span className="fc-empty-icon">◈</span>
          <p>Community not found.</p>
          <button className="fc-btn fc-btn--outline" onClick={() => navigate("/community")}>← All Communities</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fc">
      <style>{CSS}</style>

      <nav className={`fc-nav${scrolled ? " fc-nav--solid" : ""}`}>
        <div className="fc-nav-inner">
          <button className="fc-nav-back" onClick={() => navigate("/community")}>← Communities</button>
          {scrolled && community && (
            <div className="fc-nav-community">
              <div className="fc-nav-avatar">
                {community.imageUrl
                  ? <img src={community.imageUrl} alt="" onError={(e) => { e.target.style.display = "none"; }} />
                  : community.emoji || "◈"}
              </div>
              <span>{community.name}</span>
            </div>
          )}
          {userEmail
            ? <span className="fc-joined-badge">✓ Joined</span>
            : <button className="fc-btn fc-btn--gold" onClick={() => setShowJoin(true)}>+ Join</button>}
        </div>
      </nav>

      <div className="fc-page">
        <div className="fc-container">

          {/* Channel header */}
          <div className="fc-channel-header">
            <div className="fc-channel-avatar">
              {community?.imageUrl
                ? <img src={community.imageUrl} alt="" onError={(e) => { e.target.style.display = "none"; }} />
                : <span>{community?.emoji || "◈"}</span>}
            </div>
            <div className="fc-channel-info">
              <h1 className="fc-channel-title">{community?.name || "Loading…"}</h1>
              {community?.description && <p className="fc-channel-desc">{community.description}</p>}
              <span className="fc-channel-members">
                {subscriberCount.toLocaleString()} member{subscriberCount !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="fc-channel-action">
              {userEmail
                ? <span className="fc-joined-badge">✓ Joined</span>
                : <button className="fc-btn fc-btn--gold" onClick={() => setShowJoin(true)}>+ Join Channel</button>}
            </div>
          </div>

          {/* Posts */}
          {loading ? (
            <div className="fc-state">
              <div className="fc-spinner" />
              <span>Loading posts…</span>
            </div>
          ) : posts.length === 0 ? (
            <div className="fc-state">
              <span className="fc-state-icon">📬</span>
              <p>No posts yet. Check back soon.</p>
            </div>
          ) : (
            <div className="fc-feed">
              {posts.map((post, idx) => (
                <PostCard
                  key={post.id} post={post}
                  delay={idx * 0.04}
                  isLiked={likedPosts.includes(post.id)}
                  votedOption={votedPolls[post.id]}
                  onLike={() => handleLike(post.id)}
                  onVote={(optIdx) => handleVote(post.id, optIdx)}
                  onJoin={() => setShowJoin(true)}
                  authorAvatar={authorAvatar}
                />
              ))}
            </div>
          )}

        </div>
      </div>

      {showJoin && community && (
        <JoinModal
          communityId={community.id}
          onClose={() => setShowJoin(false)}
          onJoined={handleJoined}
        />
      )}
    </div>
  );
}

function PostCard({ post, delay, isLiked, votedOption, onLike, onVote, authorAvatar }) {
  const type = post.type || (post.imageUrl ? "image" : post.linkUrl ? "link" : "text");
  const date = post.createdAt?.toDate?.();
  const timeStr = date
    ? date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
    : "";

  return (
    <div className="fc-post" style={{ animationDelay: `${delay}s`, animation: `fc-in 0.3s ease ${delay}s both` }}>
      <div className="fc-post-author">
        <div className="fc-post-avatar">
          {authorAvatar ? <img src={authorAvatar} alt="" /> : "SU"}
        </div>
        <div className="fc-post-author-info">
          <span className="fc-post-author-name">Shashank Upadhyaya</span>
          {timeStr && <span className="fc-post-time">{timeStr}</span>}
        </div>
      </div>

      {post.text && (
        <div className="fc-post-body" dangerouslySetInnerHTML={{ __html: post.text }} />
      )}

      {type === "image" && post.imageUrl && (
        <img className="fc-post-image" src={post.imageUrl} alt="" onError={(e) => { e.target.style.display = "none"; }} />
      )}

      {type === "video" && post.videoUrl && <VideoBlock url={post.videoUrl} />}

      {type === "link" && post.linkUrl && (
        <a className="fc-link-card" href={post.linkUrl} target="_blank" rel="noreferrer">
          <div className="fc-link-icon">↗</div>
          <div>
            <div className="fc-link-title">{post.linkTitle || post.linkUrl}</div>
            {post.linkTitle && <div className="fc-link-url">{post.linkUrl}</div>}
          </div>
        </a>
      )}

      {type === "poll" && post.pollQuestion && (
        <PollBlock post={post} votedOption={votedOption} onVote={onVote} />
      )}

      <div className="fc-post-footer">
        <button className={`fc-like-btn${isLiked ? " fc-like-btn--active" : ""}`} onClick={onLike}>
          {isLiked ? "♥" : "♡"} <span>{post.likeCount || 0}</span>
        </button>
      </div>
    </div>
  );
}

function VideoBlock({ url }) {
  const ytId = getYouTubeId(url);
  if (ytId) {
    return (
      <div className="fc-video">
        <iframe
          src={`https://www.youtube.com/embed/${ytId}`}
          title="video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return <video className="fc-video-native" src={url} controls />;
}

function PollBlock({ post, votedOption, onVote }) {
  const hasVoted = votedOption !== undefined;
  const totalVotes = post.pollOptions?.reduce((s, o) => s + (o.votes || 0), 0) || 0;

  return (
    <div className="fc-poll">
      <p className="fc-poll-question">{post.pollQuestion}</p>
      <div className="fc-poll-options">
        {post.pollOptions?.map((opt, i) => {
          const pct = totalVotes > 0 ? Math.round(((opt.votes || 0) / totalVotes) * 100) : 0;
          const isVoted = votedOption === i;
          if (hasVoted) {
            return (
              <div key={i} className={`fc-poll-result${isVoted ? " fc-poll-result--voted" : ""}`}>
                <div className="fc-poll-bar" style={{ width: `${pct}%` }} />
                <div className="fc-poll-result-inner">
                  <span>{isVoted && "✓ "}{opt.text}</span>
                  <span className="fc-poll-pct">{pct}%</span>
                </div>
              </div>
            );
          }
          return (
            <button key={i} className="fc-poll-option" onClick={() => onVote?.(i)}>
              {opt.text}
            </button>
          );
        })}
      </div>
      <p className="fc-poll-meta">{totalVotes} vote{totalVotes !== 1 ? "s" : ""}{!hasVoted && " · Tap to vote"}</p>
    </div>
  );
}

function JoinModal({ communityId, onClose, onJoined }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const handleJoin = async () => {
    const cleanedEmail = email.trim().toLowerCase();
    const cleanedName = name.trim();
    if (!cleanedName) { setStatus("noname"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanedEmail)) { setStatus("noemail"); return; }
    setStatus("loading");
    try {
      const ref = doc(db, "communities", communityId, "subscribers", cleanedEmail);
      const existing = await getDoc(ref);
      if (existing.exists()) { setStatus("already"); return; }
      await setDoc(ref, { name: cleanedName, email: cleanedEmail, joinedAt: serverTimestamp() });
      setStatus("success");
      onJoined(cleanedEmail);
    } catch {
      setStatus("error");
    }
  };

  return (
    <div className="fc-overlay" onClick={onClose}>
      <div className="fc-modal" onClick={(e) => e.stopPropagation()}>
        {status === "success" ? (
          <div className="fc-modal-success">
            <div className="fc-modal-check">✓</div>
            <h3 className="fc-modal-title">You're in!</h3>
            <p className="fc-modal-sub">Welcome to the community. You can now like posts and vote on polls.</p>
            <button className="fc-btn fc-btn--gold" style={{ width: "100%" }} onClick={onClose}>Let's Go →</button>
          </div>
        ) : (
          <>
            <h3 className="fc-modal-title">Join the Channel</h3>
            <p className="fc-modal-sub">Get updates, like posts, and vote on polls.</p>
            <div className="fc-modal-fields">
              <div className="fc-modal-field">
                <label className="fc-modal-label">Your Name</label>
                <input className="fc-modal-input" type="text" placeholder="Enter your name"
                  value={name} onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleJoin()} />
              </div>
              <div className="fc-modal-field">
                <label className="fc-modal-label">Email Address</label>
                <input className="fc-modal-input" type="email" placeholder="your@email.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleJoin()} />
              </div>
            </div>
            {status === "noname"  && <p className="fc-modal-err">Please enter your name.</p>}
            {status === "noemail" && <p className="fc-modal-err">Please enter a valid email.</p>}
            {status === "already" && <p className="fc-modal-err fc-modal-err--info">You're already a member!</p>}
            {status === "error"   && <p className="fc-modal-err">Something went wrong. Try again.</p>}
            <div className="fc-modal-actions">
              <button className="fc-btn fc-btn--gold" onClick={handleJoin} disabled={status === "loading"}>
                {status === "loading" ? "Joining…" : "Join →"}
              </button>
              <button className="fc-btn fc-btn--ghost" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const CSS = `
  .fc {
    --bg: #0A0B0D; --card: #0E1116; --border: rgba(255,255,255,0.08);
    --text: #EDEFF2; --muted: #9AA3AF; --gold: #C7F94B;
    --gold-dim: rgba(199,249,75,0.12); --nav-h: 60px; --radius: 8px;
    background: var(--bg); color: var(--text); min-height: 100vh;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
    font-size: 15px; -webkit-font-smoothing: antialiased;
  }
  .fc *, .fc *::before, .fc *::after { box-sizing: border-box; margin: 0; padding: 0; }
  .fc a { color: inherit; text-decoration: none; }
  .fc button { cursor: pointer; font-family: inherit; }
  .fc img { display: block; }
  .fc--center { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 100vh; gap: 16px; }

  .fc-nav {
    position: fixed; top: 0; left: 0; right: 0; z-index: 50;
    height: var(--nav-h); transition: background 0.25s, border-color 0.25s, backdrop-filter 0.25s;
    border-bottom: 1px solid transparent;
  }
  .fc-nav--solid { background: rgba(10,11,13,0.95); backdrop-filter: blur(20px); border-bottom-color: var(--border); }
  .fc-nav-inner {
    max-width: 760px; margin: 0 auto; padding: 0 24px;
    height: 100%; display: flex; align-items: center; gap: 16px;
  }
  .fc-nav-back { font-size: 13px; color: var(--muted); background: none; border: none; cursor: pointer; transition: color 0.15s; padding: 0; }
  .fc-nav-back:hover { color: var(--gold); }
  .fc-nav-community { display: flex; align-items: center; gap: 8px; flex: 1; font-size: 13px; color: var(--muted); overflow: hidden; }
  .fc-nav-avatar {
    width: 22px; height: 22px; border-radius: 4px; background: var(--gold-dim);
    display: flex; align-items: center; justify-content: center; font-size: 12px; overflow: hidden; flex-shrink: 0;
  }
  .fc-nav-avatar img { width: 100%; height: 100%; object-fit: cover; }
  .fc-joined-badge {
    font-size: 12px; font-weight: 600; color: var(--gold);
    background: var(--gold-dim); border: 1px solid rgba(199,249,75,0.3);
    padding: 4px 12px; border-radius: 20px;
  }

  .fc-page { padding-top: calc(var(--nav-h) + 0px); }
  .fc-container { max-width: 760px; margin: 0 auto; padding: 0 24px 80px; }

  .fc-channel-header {
    display: flex; align-items: flex-start; gap: 16px;
    padding: 32px 0 24px; border-bottom: 1px solid var(--border); margin-bottom: 28px;
  }
  .fc-channel-avatar {
    width: 56px; height: 56px; border-radius: 14px; background: var(--gold-dim);
    border: 1px solid rgba(199,249,75,0.2); display: flex; align-items: center; justify-content: center;
    font-size: 24px; flex-shrink: 0; overflow: hidden; color: var(--gold);
  }
  .fc-channel-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .fc-channel-info { flex: 1; min-width: 0; }
  .fc-channel-title { font-family: 'Archivo', sans-serif; font-weight: 800; font-size: clamp(18px, 3vw, 24px); letter-spacing: -0.02em; margin-bottom: 4px; }
  .fc-channel-desc { font-size: 14px; color: var(--muted); line-height: 1.5; margin-bottom: 6px; }
  .fc-channel-members { font-size: 13px; color: var(--muted); }
  .fc-channel-action { flex-shrink: 0; }

  .fc-feed { display: flex; flex-direction: column; gap: 16px; }
  .fc-post {
    background: var(--card); border: 1px solid var(--border);
    border-radius: var(--radius); padding: 20px 22px;
    transition: border-color 0.2s;
    animation: fc-in 0.3s ease both;
  }
  .fc-post:hover { border-color: rgba(255,255,255,0.12); }
  @keyframes fc-in { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }

  .fc-post-author { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
  .fc-post-avatar {
    width: 36px; height: 36px; border-radius: 50%; background: var(--gold-dim);
    border: 2px solid rgba(199,249,75,0.2); display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700; color: var(--gold); overflow: hidden; flex-shrink: 0;
  }
  .fc-post-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .fc-post-author-info { display: flex; flex-direction: column; gap: 2px; }
  .fc-post-author-name { font-size: 14px; font-weight: 600; }
  .fc-post-time { font-size: 12px; color: var(--muted); }

  .fc-post-body {
    font-size: 15px; line-height: 1.75; color: rgba(237,239,242,0.85);
    margin-bottom: 14px; overflow-wrap: break-word;
  }
  .fc-post-body h2 { font-family: 'Archivo', sans-serif; font-size: 17px; font-weight: 700; margin: 12px 0 6px; color: var(--text); }
  .fc-post-body h3 { font-family: 'Archivo', sans-serif; font-size: 15px; font-weight: 600; margin: 10px 0 4px; color: var(--text); }
  .fc-post-body p { margin: 0 0 10px; }
  .fc-post-body ul, .fc-post-body ol { padding-left: 20px; margin: 0 0 10px; list-style: disc; }
  .fc-post-body ol { list-style: decimal; }
  .fc-post-body li { margin-bottom: 4px; display: list-item; }
  .fc-post-body strong { color: var(--text); font-weight: 700; }
  .fc-post-body a { color: var(--gold); text-decoration: underline; }
  .fc-post-body blockquote { border-left: 3px solid var(--gold); padding: 8px 14px; color: var(--muted); margin: 8px 0; font-style: italic; }

  .fc-post-image { width: 100%; max-height: 460px; object-fit: cover; border-radius: 6px; border: 1px solid var(--border); margin-bottom: 12px; }

  .fc-video { position: relative; padding-bottom: 56.25%; border-radius: 6px; overflow: hidden; margin-bottom: 12px; border: 1px solid var(--border); background: #000; }
  .fc-video iframe { position: absolute; inset: 0; width: 100%; height: 100%; border: none; }
  .fc-video-native { width: 100%; display: block; border-radius: 6px; margin-bottom: 12px; }

  .fc-link-card {
    display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;
    padding: 14px 16px; border: 1px solid var(--border); border-radius: 6px;
    transition: border-color 0.2s;
  }
  .fc-link-card:hover { border-color: rgba(199,249,75,0.4); }
  .fc-link-icon { font-size: 18px; color: var(--gold); flex-shrink: 0; }
  .fc-link-title { font-size: 14px; font-weight: 600; color: var(--gold); margin-bottom: 2px; }
  .fc-link-url { font-size: 12px; color: var(--muted); word-break: break-all; }

  .fc-poll { margin-bottom: 12px; background: rgba(255,255,255,0.02); border: 1px solid var(--border); border-radius: 8px; padding: 16px; }
  .fc-poll-question { font-size: 15px; font-weight: 600; margin-bottom: 12px; line-height: 1.4; }
  .fc-poll-options { display: flex; flex-direction: column; gap: 8px; }
  .fc-poll-option {
    width: 100%; border: 1px solid var(--border); border-radius: 6px;
    padding: 10px 14px; color: var(--text); font-size: 14px; text-align: left;
    background: rgba(255,255,255,0.03); transition: all 0.15s;
  }
  .fc-poll-option:hover { border-color: rgba(199,249,75,0.4); background: var(--gold-dim); }
  .fc-poll-result { position: relative; border: 1px solid var(--border); border-radius: 6px; overflow: hidden; }
  .fc-poll-result--voted { border-color: rgba(199,249,75,0.4); }
  .fc-poll-bar { position: absolute; inset: 0; background: rgba(199,249,75,0.1); transition: width 0.5s ease; pointer-events: none; }
  .fc-poll-result--voted .fc-poll-bar { background: rgba(199,249,75,0.15); }
  .fc-poll-result-inner { position: relative; padding: 10px 14px; display: flex; justify-content: space-between; align-items: center; }
  .fc-poll-result-inner span { font-size: 14px; }
  .fc-poll-pct { font-size: 13px; font-weight: 700; color: var(--gold); flex-shrink: 0; margin-left: 12px; }
  .fc-poll-meta { margin-top: 8px; font-size: 12px; color: var(--muted); }

  .fc-post-footer { display: flex; align-items: center; justify-content: flex-end; margin-top: 14px; padding-top: 12px; border-top: 1px solid var(--border); }
  .fc-like-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: none; border: 1px solid var(--border); border-radius: 20px;
    padding: 5px 14px; font-size: 13px; color: var(--muted); transition: all 0.15s;
  }
  .fc-like-btn:hover { border-color: rgba(199,249,75,0.4); color: var(--gold); }
  .fc-like-btn--active { border-color: rgba(199,249,75,0.4); color: var(--gold); background: var(--gold-dim); }

  .fc-btn {
    display: inline-flex; align-items: center; font-size: 14px; font-weight: 600;
    padding: 9px 18px; border-radius: 6px; border: none; cursor: pointer; transition: all 0.15s;
  }
  .fc-btn--gold { background: var(--gold); color: #000; }
  .fc-btn--gold:hover:not(:disabled) { opacity: 0.88; }
  .fc-btn--ghost { background: transparent; color: var(--muted); border: 1px solid var(--border); }
  .fc-btn--ghost:hover { color: var(--text); border-color: rgba(255,255,255,0.2); }
  .fc-btn--outline { background: transparent; color: var(--text); border: 1px solid var(--border); }
  .fc-btn--outline:hover { border-color: rgba(199,249,75,0.4); color: var(--gold); }

  .fc-overlay {
    position: fixed; inset: 0; z-index: 100; background: rgba(0,0,0,0.7); backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center; padding: 24px;
  }
  .fc-modal {
    width: 100%; max-width: 420px; background: var(--card);
    border: 1px solid var(--border); border-radius: 12px; padding: 32px;
    display: flex; flex-direction: column; gap: 16px;
  }
  .fc-modal-success { text-align: center; display: flex; flex-direction: column; align-items: center; gap: 16px; }
  .fc-modal-check {
    width: 56px; height: 56px; border-radius: 50%; background: var(--gold-dim);
    border: 2px solid rgba(199,249,75,0.4); display: flex; align-items: center; justify-content: center;
    font-size: 24px; color: var(--gold);
  }
  .fc-modal-title { font-family: 'Archivo', sans-serif; font-weight: 800; font-size: 20px; letter-spacing: -0.02em; }
  .fc-modal-sub { font-size: 14px; color: var(--muted); line-height: 1.6; }
  .fc-modal-fields { display: flex; flex-direction: column; gap: 12px; }
  .fc-modal-field { display: flex; flex-direction: column; gap: 5px; }
  .fc-modal-label { font-size: 13px; font-weight: 600; }
  .fc-modal-input {
    background: rgba(255,255,255,0.04); border: 1px solid var(--border);
    border-radius: 6px; color: var(--text); outline: none;
    padding: 10px 14px; font-size: 14px; font-family: inherit; width: 100%;
    transition: border-color 0.15s;
  }
  .fc-modal-input::placeholder { color: var(--muted); }
  .fc-modal-input:focus { border-color: var(--gold); }
  .fc-modal-err { font-size: 12px; color: #ef4444; }
  .fc-modal-err--info { color: var(--gold); }
  .fc-modal-actions { display: flex; gap: 10px; }
  .fc-modal-actions .fc-btn { flex: 1; justify-content: center; }

  .fc-state {
    text-align: center; padding: 80px 0; display: flex; flex-direction: column;
    align-items: center; gap: 14px; font-size: 14px; color: var(--muted);
  }
  .fc-state-icon { font-size: 32px; }
  .fc-spinner { width: 28px; height: 28px; border-radius: 50%; border: 2px solid var(--border); border-top-color: var(--gold); animation: fc-spin 0.75s linear infinite; }
  @keyframes fc-spin { to { transform: rotate(360deg); } }
  .fc-empty { text-align: center; display: flex; flex-direction: column; align-items: center; gap: 16px; }
  .fc-empty-icon { font-size: 36px; color: var(--muted); }
  .fc-empty p { font-size: 15px; color: var(--muted); }

  @media (max-width: 600px) {
    .fc-channel-header { flex-wrap: wrap; }
    .fc-channel-action { order: 3; width: 100%; }
    .fc-nav-community { display: none; }
    .fc-post { padding: 16px; }
    .fc-modal { padding: 20px 16px; }
    .fc-overlay { padding: 16px; }
    .fc-nav-inner { padding: 0 16px; }
    .fc-container { padding: 0 16px 48px; }
    .site-logo-img { height: 40px; }
  }
  @media (max-width: 400px) {
    .fc-modal-actions { flex-direction: column; }
    .fc-modal-actions .fc-btn { width: 100%; }
  }
`;
